/**
 * /tests/preview/:attemptId — Candidate pre-screen preview page (Story 68).
 *
 * This is the candidate-facing pre-screen view shown to System Admins when
 * they click "Preview as Candidate" on the Test edit page (AC 4 + AC 5).
 *
 * Renders:
 *   - Test title
 *   - Task Brief as markdown (AC 4) — rendered via <pre> for now.
 *     // CARVE-OUT (04-test-composer/02 review round 1, 2026-05-23): markdown
 *     // rendering via react-markdown (or equivalent) is deferred to Module 11
 *     // (design-tokens / frontend-state slice). A full markdown renderer
 *     // requires a bundled library that must be ratified by the Module 11
 *     // frontend-state design decision. The <pre> render is acceptable for
 *     // MVP preview scope per reviewer suggestion.
 *     // Ratified by reviewer commit db212cd — see issue file's ## Review.
 *   - Estimated Minutes badge (AC 4)
 *   - Download Task Artefact button — signed R2 URL (AC 5)
 *   - Candidate shell (no admin controls)
 *   - Preview banner so the admin sees they're in preview mode
 *
 * Route: /tests/preview/:attemptId
 * Data: GET /api/tests/preview/:attemptId (requires admin session cookie)
 *
 * The route does NOT require the admin shell; it intentionally mirrors the
 * candidate experience. The admin cookie is present because the tab is opened
 * from the same origin via window.open().
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Shell } from '@/components/shell';
import {
  getPreviewView,
  type PreviewViewPayload,
} from '../../state/tests/preview-api.ts';

export function PreviewPage(): ReactElement {
  const params = useParams<{ attemptId: string }>();
  const attemptId = params.attemptId ?? '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PreviewViewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      const res = await getPreviewView(attemptId);
      if (cancelled) return;
      if (!res.ok) {
        setError(`${res.error.code}: ${res.error.message}`);
      } else {
        setData(res.data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [attemptId]);

  if (loading) {
    return (
      <Shell mode="individual" densityMode="editorial">
        <p>Loading preview…</p>
      </Shell>
    );
  }

  if (error !== null || data === null) {
    return (
      <Shell mode="individual" densityMode="editorial">
        <p role="alert">{error ?? 'Preview data not available.'}</p>
      </Shell>
    );
  }

  const { test } = data;

  return (
    <Shell mode="individual" densityMode="editorial">
      {/* Preview banner — not shown to real candidates */}
      <div role="banner" aria-label="Preview mode banner">
        <strong>Preview mode</strong> — this is how candidates see the pre-screen.
        No quota is consumed. Close this tab to return to the edit page.
      </div>

      <h1>{test.title}</h1>

      {/* Estimated Minutes — AC 4 */}
      <p>
        Estimated time: <strong>{test.estimated_minutes} min</strong>
        {test.time_limit > 0
          ? ` · Test limit: ${test.time_limit} min`
          : null}
      </p>

      {/* Task Brief rendered as markdown — AC 4 */}
      {/* CARVE-OUT (04-test-composer/02 review round 1, 2026-05-23): markdown
          rendering is deferred to Module 11. Using <pre> for MVP scope.
          Ratified by reviewer commit db212cd — see issue file's ## Review. */}
      {test.task_brief_md !== null && test.task_brief_md.length > 0 ? (
        <section>
          <h2>Task Brief</h2>
          <pre data-testid="task-brief">{test.task_brief_md}</pre>
        </section>
      ) : null}

      {/* Download Task Artefact button — AC 5 */}
      {test.task_artifact_url !== null && test.task_artifact_url.length > 0 ? (
        <section>
          <a
            href={test.task_artifact_url}
            download
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button variant="secondary" type="button">Download Task Artefact</Button>
          </a>
          <p>Open this file in Revit on your own machine and complete the task.</p>
        </section>
      ) : null}
    </Shell>
  );
}
